/**
 * Couche de fond du tapis de jeu.
 *
 * Textures statiques (pré-générées, seamless ou centrées) :
 *   - cards-backs / cards-all / cards-trumps (tiled, dark/light auto)
 *   - kaleidoscope / kaleidoscope-v2 (centré, cover)
 *   Voir scripts/generate-table-textures.mjs / generate-kaleidoscope.mjs.
 *
 * Surcouche animée :
 *   - cards-plis : cartes ajoutées pli par pli, empilées en tas
 *     par joueur. Se superpose à la texture de base.
 *
 * Emblème optionnel (PNG centré, incompatible avec kaléidoscopes).
 */

import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import type { TableBase, TableOverlay, TableEmblem, DuplicateTableStyle } from '../types/app';
import type { CardNumber } from '../types/game';
import { getCardBackImagePath, generateDeck } from '../utils/cards';
import { computeTableLayout } from '../utils/tableLayout';
import DuplicateTableDecor from './DuplicateTableDecor';
import '../styles/duplicate-table.css';

// ============================================
// Types exports
// ============================================

export type ScreenPosition = 'south' | 'north' | 'east' | 'west' | 'north-east' | 'north-west';

export interface TrickEndEvent {
  winnerScreenPos: ScreenPosition;
  cardCount: number;
}

export interface ChienEvent {
  takerScreenPos: ScreenPosition;
  cardCount: number;
  excludeTaker?: boolean;
}

interface TableBackgroundLayerProps {
  base: TableBase;
  overlay: TableOverlay;
  emblem?: TableEmblem;
  isDarkMode: boolean;
  duplicateStyle?: DuplicateTableStyle;
  trickEvents?: TrickEndEvent[];
  chienEvent?: ChienEvent;
  gameEnded?: boolean;
  /** 5p : fusionner le tas du partenaire révélé avec celui du preneur */
  partnerPileMerge?: {
    partnerScreenPos: ScreenPosition;
    takerScreenPos: ScreenPosition;
  };
}

// Theme-aware tiled texture URLs
const TILED_URLS: Record<string, { light: string; dark: string }> = {
  'cards-backs':  { light: '/backgrounds/table-backs.jpg',  dark: '/backgrounds/table-backs-dark.jpg' },
  'cards-all':    { light: '/backgrounds/table-all.jpg',    dark: '/backgrounds/table-all-dark.jpg' },
  'cards-trumps': { light: '/backgrounds/table-trumps.jpg', dark: '/backgrounds/table-trumps-dark.jpg' },
};

// Centered texture URLs (identical in both themes)
const CENTERED_URLS: Partial<Record<TableBase, string>> = {
  'kaleidoscope':    '/backgrounds/table-kaleidoscope.jpg',
  'kaleidoscope-v2': '/backgrounds/table-kaleidoscope-v2.jpg',
};

// Emblem images (PNG with transparency, centered)
const EMBLEM_MAP: Partial<Record<TableEmblem, string>> = {
  'wt-v1': '/backgrounds/wt-emblem.png',
  'wt-v2': '/backgrounds/wt-emblem-v2.png',
  'wt-v3': '/backgrounds/wt-emblem-v3.png',
};

const KALEIDOSCOPE_BASES = new Set<TableBase>(['kaleidoscope', 'kaleidoscope-v2']);

function getTextureUrl(base: TableBase, isDark: boolean): { url: string; mode: 'tiled' | 'centered' } | null {
  if (base in TILED_URLS) {
    const entry = TILED_URLS[base];
    return { url: isDark ? entry.dark : entry.light, mode: 'tiled' };
  }
  const centered = CENTERED_URLS[base];
  if (centered) return { url: centered, mode: 'centered' };
  return null;
}

// ============================================
// Dynamic pile card (abstract/relative data)
// ============================================

interface PileCardData {
  cardNumber: CardNumber;
  pilePos: ScreenPosition;
  stackIndex: number;
  /** Random offsets as fractions of card size */
  offsetFracX: number;
  offsetFracY: number;
  /** Final pile rotation (small, ±6°) added to base rotation */
  pileRot: number;
  zIndex: number;
  /** Scatter position as fraction of viewport */
  scatterFracX: number;
  scatterFracY: number;
  scatterRot: number;
  delay: number;
}

// ============================================
// Approximate TrickDisplay layout (mirrors its logic)
// ============================================

function getResponsiveCardSize(viewW: number): { w: number; h: number } {
  if (viewW <= 480) return { w: 70, h: 127 };
  if (viewW <= 768) return { w: 90, h: 164 };
  return { w: 120, h: 218 };
}

interface TrickLayoutApprox {
  northZoneY: number;
  southZoneY: number;
  middleZoneY: number;
  cardW: number;
  cardH: number;
  sideMargin: number;
}

/**
 * Replicates the key vertical positions from TrickDisplay's layout
 * so pile positions can track trick card positions exactly.
 */
function getTrickLayoutApprox(viewW: number, viewH: number): TrickLayoutApprox {
  const { h: maxCardHeight } = getResponsiveCardSize(viewW);
  const orientationAttr = typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-orientation')
    : null;
  const layout = computeTableLayout({
    containerWidth: viewW,
    containerHeight: viewH,
    handCount: 18,
    handLayout: 'flat',
    maxCardHeight,
    orientation: orientationAttr === 'portrait' || orientationAttr === 'landscape'
      ? orientationAttr
      : undefined,
  });
  return {
    northZoneY: layout.northZoneY,
    southZoneY: layout.southZoneY,
    middleZoneY: layout.middleZoneY,
    cardW: layout.cardWidth,
    cardH: layout.cardHeight,
    sideMargin: layout.sideMargin,
  };
}

// ============================================
// Pile metadata — where each player's pile sits
// ============================================

interface PileMeta {
  /** Center X in pixels */
  cx: number;
  /** Center Y in pixels */
  cy: number;
  /** Base rotation: 0° for north/south, 90° for east/west */
  baseRot: number;
}

/**
 * Computes pile center positions relative to actual trick card
 * positions. Piles sit at the table edge (half off-screen) near
 * each player, and East/West piles are rotated 90°.
 *
 * Positioning per player:
 *   - North:  top edge, shifted left of center (to Nord's right)
 *   - East:   right edge, above Est's trick card
 *   - West:   left edge, below Ouest's trick card
 *   - South:  just below Sud's trick card, above the hand
 */
function getPileMeta(
  pos: ScreenPosition,
  layout: TrickLayoutApprox,
  viewW: number,
  _viewH: number,
): PileMeta {
  const { southZoneY, middleZoneY, cardH } = layout;

  switch (pos) {
    case 'south':
      return {
        cx: viewW * 0.55 + cardH * 0.25,
        cy: southZoneY + cardH * 1.7,
        baseRot: 0,
      };
    case 'north':
      return { cx: viewW * 0.38, cy: 0, baseRot: 0 };
    case 'east':
      return {
        cx: viewW,
        cy: middleZoneY - cardH * 0.3,
        baseRot: 90,
      };
    case 'west':
      return {
        cx: 0,
        cy: middleZoneY + cardH * 1.3,
        baseRot: 90,
      };
    case 'north-east':
      return { cx: viewW * 0.80, cy: 0, baseRot: 0 };
    case 'north-west':
      return { cx: viewW * 0.20, cy: 0, baseRot: 0 };
  }
}

// ============================================
// Seeded PRNG
// ============================================

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle<T>(array: T[], rng: () => number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ============================================
// Dynamic pile card builders
// ============================================

function getScatterZone(pos: ScreenPosition): {
  x0: number; y0: number; x1: number; y1: number;
} {
  switch (pos) {
    case 'south':      return { x0: 0.20, y0: 0.60, x1: 0.80, y1: 0.95 };
    case 'north':      return { x0: 0.20, y0: 0.05, x1: 0.80, y1: 0.35 };
    case 'east':       return { x0: 0.60, y0: 0.15, x1: 0.95, y1: 0.85 };
    case 'west':       return { x0: 0.05, y0: 0.15, x1: 0.40, y1: 0.85 };
    case 'north-east': return { x0: 0.50, y0: 0.05, x1: 0.95, y1: 0.45 };
    case 'north-west': return { x0: 0.05, y0: 0.05, x1: 0.50, y1: 0.45 };
  }
}

function buildPileCards(
  pos: ScreenPosition,
  count: number,
  rng: () => number,
  baseZ: number,
  pileIndex: number,
): PileCardData[] {
  const deck = generateDeck();
  const shuffled = seededShuffle(deck, rng);
  const scatter = getScatterZone(pos);
  const cards: PileCardData[] = [];

  for (let i = 0; i < count; i++) {
    const stackIdx = pileIndex + i;
    cards.push({
      cardNumber: shuffled[i % shuffled.length],
      pilePos: pos,
      stackIndex: stackIdx,
      offsetFracX: (rng() - 0.5) * 0.14,
      offsetFracY: (rng() - 0.5) * 0.10 - stackIdx * 0.008,
      pileRot: (rng() - 0.5) * 12,
      zIndex: baseZ + i,
      scatterFracX: scatter.x0 + rng() * (scatter.x1 - scatter.x0),
      scatterFracY: scatter.y0 + rng() * (scatter.y1 - scatter.y0),
      scatterRot: (rng() - 0.5) * 120,
      delay: i * 80,
    });
  }
  return cards;
}

function buildChienCards(
  positions: ScreenPosition[],
  count: number,
  rng: () => number,
  baseZ: number,
): PileCardData[] {
  const deck = generateDeck();
  const shuffled = seededShuffle(deck, rng);
  const cards: PileCardData[] = [];

  for (let i = 0; i < count; i++) {
    const pos = positions[Math.floor(rng() * positions.length)];
    const scatter = getScatterZone(pos);
    cards.push({
      cardNumber: shuffled[i % shuffled.length],
      pilePos: pos,
      stackIndex: 0,
      offsetFracX: (rng() - 0.5) * 0.14,
      offsetFracY: (rng() - 0.5) * 0.10,
      pileRot: (rng() - 0.5) * 12,
      zIndex: baseZ + i,
      scatterFracX: scatter.x0 + rng() * (scatter.x1 - scatter.x0),
      scatterFracY: scatter.y0 + rng() * (scatter.y1 - scatter.y0),
      scatterRot: (rng() - 0.5) * 120,
      delay: i * 80,
    });
  }
  return cards;
}

// ============================================
// Compute pixel position from abstract pile data
// ============================================

function computePilePosition(
  card: PileCardData,
  layout: TrickLayoutApprox,
  viewW: number,
  viewH: number,
  cardW: number,
  cardH: number,
): { x: number; y: number; rot: number } {
  const meta = getPileMeta(card.pilePos, layout, viewW, viewH);
  return {
    x: meta.cx - cardW / 2 + card.offsetFracX * cardW,
    y: meta.cy - cardH / 2 + card.offsetFracY * cardH,
    rot: meta.baseRot + card.pileRot,
  };
}

// ============================================
// Sub-components
// ============================================

/**
 * A pile card that is still animating (fly → scatter → pile).
 * Uses CSS animation with absolute pixel values computed from
 * current viewport dimensions.
 */
const AnimatingPileTile = memo(function AnimatingPileTile({
  card,
  layout,
  viewW,
  viewH,
  cardW,
  cardH,
}: {
  card: PileCardData;
  layout: TrickLayoutApprox;
  viewW: number;
  viewH: number;
  cardW: number;
  cardH: number;
}) {
  const pile = computePilePosition(card, layout, viewW, viewH, cardW, cardH);
  const fromX = viewW / 2 - cardW / 2;
  const fromY = viewH / 2 - cardH / 2;
  const scatterX = card.scatterFracX * viewW - cardW / 2;
  const scatterY = card.scatterFracY * viewH - cardH / 2;

  return (
    <div
      className="table-bg-tile table-bg-tile-animated"
      style={{
        '--tile-from-x': `${fromX}px`,
        '--tile-from-y': `${fromY}px`,
        '--tile-scatter-x': `${scatterX}px`,
        '--tile-scatter-y': `${scatterY}px`,
        '--tile-scatter-rot': `${card.scatterRot}deg`,
        '--tile-pile-x': `${pile.x}px`,
        '--tile-pile-y': `${pile.y}px`,
        '--tile-pile-rot': `${pile.rot}deg`,
        width: cardW,
        height: cardH,
        zIndex: card.zIndex,
        animationDelay: `${card.delay}ms`,
      } as React.CSSProperties}
    >
      <img src={getCardBackImagePath()} alt="" draggable={false} />
    </div>
  );
});

/**
 * A pile card that has finished animating.
 * Position is recomputed from viewport dimensions on every render,
 * so it naturally adapts on resize. CSS transition smooths the move.
 */
const SettledPileTile = memo(function SettledPileTile({
  card,
  layout,
  viewW,
  viewH,
  cardW,
  cardH,
}: {
  card: PileCardData;
  layout: TrickLayoutApprox;
  viewW: number;
  viewH: number;
  cardW: number;
  cardH: number;
}) {
  const pile = computePilePosition(card, layout, viewW, viewH, cardW, cardH);

  return (
    <div
      className="table-bg-tile table-bg-tile-settled"
      style={{
        left: pile.x,
        top: pile.y,
        width: cardW,
        height: cardH,
        transform: `rotate(${pile.rot}deg)`,
        zIndex: card.zIndex,
      }}
    >
      <img src={getCardBackImagePath()} alt="" draggable={false} />
    </div>
  );
});

// ============================================
// Main component
// ============================================

const TableBackgroundLayer = memo(function TableBackgroundLayer({
  base,
  overlay,
  emblem = 'none',
  isDarkMode,
  duplicateStyle,
  trickEvents = [],
  chienEvent,
  gameEnded = false,
  partnerPileMerge,
}: TableBackgroundLayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setDimensions({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const texture = getTextureUrl(base, isDarkMode);
  const isDynamic = overlay === 'cards-plis';

  // Responsive card size for dynamic mode
  const cardSize = useMemo(
    () => (dimensions ? getResponsiveCardSize(dimensions.w) : { w: 120, h: 218 }),
    [dimensions],
  );

  // Approximate trick display layout for pile positioning
  const trickLayout = useMemo(
    () => (dimensions ? getTrickLayoutApprox(dimensions.w, dimensions.h) : null),
    [dimensions],
  );

  // ---- Dynamic mode (cards-plis) ----
  const dynamicRngSeed = useRef(Date.now());
  const pileCardsRef = useRef<PileCardData[]>([]);
  const processedTricks = useRef(0);
  const processedChien = useRef(false);
  const pileCountsRef = useRef<Map<ScreenPosition, number>>(new Map());
  const mergedPartnerPileRef = useRef(false);
  const [pileCards, setPileCards] = useState<PileCardData[]>([]);
  /** Index up to which cards are "settled" (animation done) */
  const [settledUpTo, setSettledUpTo] = useState(0);
  const settleTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const resetDynamic = useCallback(() => {
    pileCardsRef.current = [];
    processedTricks.current = 0;
    processedChien.current = false;
    pileCountsRef.current = new Map();
    mergedPartnerPileRef.current = false;
    dynamicRngSeed.current = Date.now();
    settleTimers.current.forEach(clearTimeout);
    settleTimers.current = [];
    setPileCards([]);
    setSettledUpTo(0);
  }, []);

  useEffect(() => {
    if (gameEnded && isDynamic) resetDynamic();
  }, [gameEnded, isDynamic, resetDynamic]);

  useEffect(() => {
    resetDynamic();
  }, [overlay, resetDynamic]);

  /** Regrouper les plis déjà amassés par le partenaire avec ceux du preneur */
  useEffect(() => {
    if (!isDynamic || !partnerPileMerge || mergedPartnerPileRef.current) return;
    const { partnerScreenPos, takerScreenPos } = partnerPileMerge;
    if (partnerScreenPos === takerScreenPos) {
      mergedPartnerPileRef.current = true;
      return;
    }

    mergedPartnerPileRef.current = true;

    const partnerStack = pileCountsRef.current.get(partnerScreenPos) ?? 0;
    const takerStack = pileCountsRef.current.get(takerScreenPos) ?? 0;
    if (partnerStack > 0) {
      pileCountsRef.current.set(takerScreenPos, takerStack + partnerStack);
      pileCountsRef.current.delete(partnerScreenPos);
    }

    const remapped = pileCardsRef.current.map((card) =>
      card.pilePos === partnerScreenPos
        ? { ...card, pilePos: takerScreenPos }
        : card,
    );
    const didRemap = remapped.some(
      (card, i) => card.pilePos !== pileCardsRef.current[i].pilePos,
    );
    if (didRemap || partnerStack > 0) {
      pileCardsRef.current = remapped;
      setPileCards([...remapped]);
    }
  }, [isDynamic, partnerPileMerge]);

  const allPositions = useMemo((): ScreenPosition[] => {
    const seen = new Set<ScreenPosition>();
    for (const ev of trickEvents) seen.add(ev.winnerScreenPos);
    if (chienEvent) seen.add(chienEvent.takerScreenPos);
    if (seen.size === 0) return ['south', 'east', 'north', 'west'];
    return Array.from(seen);
  }, [trickEvents, chienEvent]);

  const processDynamicEvents = useCallback(() => {
    if (!isDynamic || !dimensions) return;

    let changed = false;
    const rng = mulberry32(dynamicRngSeed.current);
    const existingCount = pileCardsRef.current.length;
    for (let i = 0; i < existingCount * 8; i++) rng();
    const batchStart = pileCardsRef.current.length;

    // Chien
    if (chienEvent && !processedChien.current) {
      processedChien.current = true;
      const baseZ = pileCardsRef.current.length;
      if (chienEvent.excludeTaker) {
        const positions = allPositions.filter((p) => p !== chienEvent.takerScreenPos);
        const newCards = buildChienCards(positions, chienEvent.cardCount, rng, baseZ);
        pileCardsRef.current = [...pileCardsRef.current, ...newCards];
      } else {
        const pos = chienEvent.takerScreenPos;
        const pileIdx = pileCountsRef.current.get(pos) ?? 0;
        const newCards = buildPileCards(pos, chienEvent.cardCount, rng, baseZ, pileIdx);
        pileCountsRef.current.set(pos, pileIdx + chienEvent.cardCount);
        pileCardsRef.current = [...pileCardsRef.current, ...newCards];
      }
      changed = true;
    }

    // Tricks
    while (processedTricks.current < trickEvents.length) {
      const ev = trickEvents[processedTricks.current];
      processedTricks.current++;
      const pos = ev.winnerScreenPos;
      const baseZ = pileCardsRef.current.length;
      const pileIdx = pileCountsRef.current.get(pos) ?? 0;
      const newCards = buildPileCards(pos, ev.cardCount, rng, baseZ, pileIdx);
      pileCountsRef.current.set(pos, pileIdx + ev.cardCount);
      pileCardsRef.current = [...pileCardsRef.current, ...newCards];
      changed = true;
    }

    if (changed) {
      const allCards = [...pileCardsRef.current];
      setPileCards(allCards);

      // Schedule settle transition after animations complete
      const batchEnd = allCards.length;
      if (batchEnd > batchStart) {
        const maxDelay = Math.max(...allCards.slice(batchStart).map((c) => c.delay));
        const settleTime = 1300 + maxDelay;
        const timer = setTimeout(() => {
          setSettledUpTo((prev) => Math.max(prev, batchEnd));
        }, settleTime);
        settleTimers.current.push(timer);
      }
    }
  }, [isDynamic, dimensions, trickEvents, chienEvent, allPositions]);

  useEffect(() => {
    processDynamicEvents();
  }, [processDynamicEvents]);

  const emblemUrl = !KALEIDOSCOPE_BASES.has(base) && emblem !== 'none'
    ? EMBLEM_MAP[emblem]
    : undefined;

  const hasAnything = texture || isDynamic || emblemUrl || duplicateStyle;
  if (!hasAnything) return null;

  return (
    <div className="table-bg-layer" ref={containerRef} aria-hidden>
      {texture?.mode === 'tiled' && (
        <div
          className="table-bg-static table-bg-static-tiled"
          style={{ backgroundImage: `url(${texture.url})` }}
        />
      )}
      {texture?.mode === 'centered' && (
        <div
          className="table-bg-static table-bg-static-centered"
          style={{ backgroundImage: `url(${texture.url})` }}
        />
      )}
      {emblemUrl && (
        <div
          className="table-bg-emblem"
          style={{ backgroundImage: `url(${emblemUrl})` }}
        />
      )}
      {duplicateStyle && <DuplicateTableDecor style={duplicateStyle} />}
      {isDynamic && dimensions && trickLayout && (
        <div className="table-bg-tiles table-bg-tiles-dynamic">
          {pileCards.map((card, i) =>
            i < settledUpTo ? (
              <SettledPileTile
                key={`p-${i}`}
                card={card}
                layout={trickLayout}
                viewW={dimensions.w}
                viewH={dimensions.h}
                cardW={cardSize.w}
                cardH={cardSize.h}
              />
            ) : (
              <AnimatingPileTile
                key={`a-${i}`}
                card={card}
                layout={trickLayout}
                viewW={dimensions.w}
                viewH={dimensions.h}
                cardW={cardSize.w}
                cardH={cardSize.h}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
});

export default TableBackgroundLayer;
